/**
 * Grammarly: AI Writing Assistant and Grammar Checker App
 *
 * Detectable two independent ways, and the behavioural route is the better one.
 *
 * Grammarly announces itself on every page it runs on:
 *
 *   <body data-gr-ext-installed data-new-gr-c-s-check-loaded data-new-gr-c-s-loaded>
 *   <grammarly-extension>            custom elements injected near editable fields
 *   <grammarly-desktop-integration>
 *
 * `data-gr-ext-installed` is not a side effect that merely happens to be observable —
 * it is a flag stating the extension is installed, written onto the page body. Grammarly
 * uses it to avoid double-initialising; any other script can read it just as easily.
 *
 * These markers are preferred over the resource probes: they survive file renames and
 * do not need a chrome-extension:// request, so a restrictive page CSP cannot suppress
 * them. The resource probe is kept as corroboration, because behavioural signals only
 * fire while the extension is actively running on the page.
 *
 * Behaviour observed on version 14.1312.0 via `tooling/batch-interactions.js`.
 */

import { probeFetch, probeSelector, probeBait } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kbfnbcaeplbcioakkpcpgfkobkghlhen';

export const grammarly: ExtensionDefinition = {
  id: ID,
  name: 'Grammarly: AI Writing Assistant and Grammar Checker App',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 35_000_000,
  notes:
    'Writes data-gr-ext-installed onto <body> — an explicit "I am installed" flag any ' +
    'script can read. Also exposes 14 static resources, so it is detectable twice over.',

  signals: [
    {
      id: 'dom-body-ext-installed',
      kind: 'dom',
      description:
        'Check <body data-gr-ext-installed>. Grammarly sets this so it does not ' +
        'double-initialise; it is readable by any script on the page.',
      weight: 1,
      run: async () => {
        const attrs = [...document.body.attributes]
          .map((a) => a.name)
          .filter((n) => n.startsWith('data-gr-') || n.startsWith('data-new-gr-'));
        return {
          fired: attrs.length > 0,
          detail: attrs.length
            ? `<body> carries ${attrs.join(', ')}`
            : 'no data-gr-* attributes on <body>',
        };
      },
    },
    {
      id: 'dom-custom-elements',
      kind: 'dom',
      description:
        'Look for Grammarly\'s injected custom elements (<grammarly-extension>, ' +
        '<grammarly-desktop-integration>), which carry the vendor prefix in the tag name.',
      weight: 1,
      run: () =>
        probeSelector('grammarly-extension, grammarly-desktop-integration, grammarly-popups', {
          timeoutMs: 1500,
        }),
    },
    {
      id: 'dom-bait-editable-attach',
      kind: 'dom',
      description:
        'Insert a bait <textarea> and see whether Grammarly attaches to it — it decorates ' +
        'editable fields it finds, so a newly inserted field draws a reaction.',
      weight: 0.5,
      run: () =>
        probeBait(
          '<textarea rows="3" cols="40"></textarea>',
          (el) => {
            el.focus();
            el.dispatchEvent(new Event('input', { bubbles: true }));
            const marked = [...el.attributes].map((a) => a.name).filter((n) => n.startsWith('data-gr'));
            const nearby = document.querySelector('grammarly-extension');
            const fired = marked.length > 0 || !!nearby;
            return {
              fired,
              detail: fired
                ? `bait textarea drew ${marked.join(', ') || '<grammarly-extension>'}`
                : 'bait textarea was not decorated',
            };
          },
          { waitMs: 2500 },
        ),
    },
    {
      id: 'war-inkwell-index',
      kind: 'web-accessible-resource',
      description:
        'Fetch src/inkwell/index.html — the Inkwell editor UI. Corroborates the DOM ' +
        'signals, and still fires when Grammarly is installed but idle on this page.',
      weight: 0.5,
      run: () => probeFetch(ID, 'src/inkwell/index.html'),
    },
  ],
};
